import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AlertCircle, Pencil, Plus, Trash2, Users, X } from "lucide-react";
import { api } from "@/lib/api";
import {
  RepresentanteFormFields,
  normalizarRepresentante,
  validarRepresentante,
} from "@/components/RepresentanteFormFields";
import type {
  BorradorPaso3Request,
  MiRegistroLegalResumen,
  RepresentanteBorrador,
} from "../types";
import { BotonesPaso } from "./Paso1Datos";

/**
 * Paso 3: representantes legales (sólo persona MORAL). CRUD en memoria con
 * modal; al guardar, el PATCH reemplaza la lista completa de RL activos del
 * RL — estrategia REPLACE simple del wizard.
 *
 * V114: identidad capturada como nombres+apellidos (no nombreCompleto único) y
 * domicilio estructurado con cascada SEPOMEX.
 */
export function Paso3Representantes({
  rl,
  onSaved,
  onSalir,
  onAtras,
}: {
  rl: MiRegistroLegalResumen;
  onSaved: () => void;
  onSalir: () => void;
  onAtras: () => void;
}) {
  const qc = useQueryClient();
  const [reps, setReps] = useState<RepresentanteBorrador[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (rl.tipoPersona === "FISICA") {
    return (
      <div className="rounded-lg border bg-estado-info-bg p-4 text-sm text-estado-info">
        Las personas físicas no requieren representantes legales. Puedes continuar al paso 4.
        <div className="mt-3">
          <button
            type="button"
            onClick={onSaved}
            className="rounded bg-primary px-4 py-1.5 text-sm font-medium text-white hover:bg-primary-hover"
          >
            Continuar a revisión
          </button>
        </div>
      </div>
    );
  }

  const abrirNuevo = () => {
    setEditIndex(null);
    setModalOpen(true);
  };
  const abrirEditar = (i: number) => {
    setEditIndex(i);
    setModalOpen(true);
  };
  const cerrar = () => {
    setModalOpen(false);
    setEditIndex(null);
  };
  const guardarRep = (rep: RepresentanteBorrador) => {
    setReps((prev) => {
      if (editIndex == null) return [...prev, rep];
      const next = [...prev];
      next[editIndex] = rep;
      return next;
    });
    cerrar();
  };
  const eliminarRep = (i: number) => {
    setReps((prev) => prev.filter((_, idx) => idx !== i));
  };

  const guardar = useMutation({
    mutationFn: async () => {
      const body: BorradorPaso3Request = { representantes: reps };
      await api.patch(`/api/mi-registro-legal/borrador/${rl.id}/paso/3`, body);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["mi-registro-legal"] });
      onSaved();
    },
    onError: (e: unknown) => {
      const r = e as { response?: { data?: { detail?: string; title?: string } } };
      setError(r.response?.data?.detail || r.response?.data?.title || (e as Error).message);
    },
  });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (reps.length === 0) {
      setError("Persona moral requiere al menos un representante legal.");
      return;
    }
    setError(null);
    guardar.mutate();
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <section className="rounded-lg border bg-surface shadow-card">
        <div className="flex items-center justify-between border-b px-4 py-3">
          <div>
            <h2 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-fg-subtle">
              <Users className="h-4 w-4" /> Representantes legales
            </h2>
            <p className="mt-0.5 text-xs text-fg-subtle">
              Mínimo uno. El PDF del poder notarial se carga al final.
            </p>
          </div>
          <button
            type="button"
            onClick={abrirNuevo}
            className="inline-flex items-center gap-1 rounded bg-primary px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-hover"
          >
            <Plus className="h-4 w-4" /> Agregar
          </button>
        </div>

        {reps.length === 0 ? (
          <div className="p-6 text-center text-sm text-fg-subtle">
            Aún no has agregado representantes legales.
          </div>
        ) : (
          <div className="w-full overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-surface-2 text-xs uppercase text-fg-subtle">
              <tr>
                <th className="px-4 py-2 text-left">Nombre completo</th>
                <th className="px-4 py-2 text-left">RFC</th>
                <th className="px-4 py-2 text-left">CURP</th>
                <th className="px-4 py-2 text-left">Correo</th>
                <th className="w-24 px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {reps.map((r, i) => (
                <tr key={i} className="border-t">
                  <td className="px-4 py-2 text-fg">{formatNombre(r)}</td>
                  <td className="px-4 py-2 font-mono text-xs text-fg-muted">{r.rfc ?? "—"}</td>
                  <td className="px-4 py-2 font-mono text-xs text-fg-muted">{r.curp ?? "—"}</td>
                  <td className="px-4 py-2 text-fg-muted">{r.correo ?? "—"}</td>
                  <td className="px-4 py-2 text-right">
                    <button
                      type="button"
                      onClick={() => abrirEditar(i)}
                      className="rounded p-1 text-fg-muted hover:bg-surface-2"
                      aria-label="Editar"
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => eliminarRep(i)}
                      className="rounded p-1 text-estado-err hover:bg-estado-err-bg"
                      aria-label="Eliminar"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </section>

      {error && (
        <div className="flex items-start gap-2 rounded-lg border bg-estado-warn-bg p-3 text-sm text-estado-warn">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <BotonesPaso
        loading={guardar.isPending}
        labelSiguiente="Guardar y revisar"
        onSalir={onSalir}
        onAtras={onAtras}
      />

      {modalOpen && (
        <ModalRepresentante
          initial={editIndex != null ? reps[editIndex] : undefined}
          onCancel={cerrar}
          onSave={guardarRep}
        />
      )}
    </form>
  );
}

function formatNombre(r: RepresentanteBorrador): string {
  const ap = (r.apellidoPaterno ?? "").trim();
  const am = (r.apellidoMaterno ?? "").trim();
  const nom = (r.nombres ?? "").trim();
  const partes = [ap, am, nom].filter(Boolean);
  if (partes.length > 0) return partes.join(" ");
  return (r.nombreCompleto ?? "").trim() || "(sin nombre)";
}

function ModalRepresentante({
  initial,
  onCancel,
  onSave,
}: {
  initial?: RepresentanteBorrador;
  onCancel: () => void;
  onSave: (r: RepresentanteBorrador) => void;
}) {
  const [r, setR] = useState<RepresentanteBorrador>(
    initial ?? {
      nombres: "",
      apellidoPaterno: "",
      apellidoMaterno: "",
      nombreCompleto: "",
      rfc: "",
      curp: "",
      correo: "",
      telefono: "",
      telefonoExtension: "",
      domicilioCalle: "",
      domicilioNumeroExterior: "",
      domicilioNumeroInterior: "",
      domicilioCodigoPostal: "",
      domicilioColonia: "",
      domicilioMunicipio: "",
      domicilioEntidadFederativa: "",
      domicilioPais: "MEX",
      domicilioLibre: "",
      consentimientoNotificacionElectronica: false,
      instrumentoPoderDescripcion: "",
      vigenciaInicio: new Date().toISOString(),
      vigenciaFin: null,
    },
  );
  const [err, setErr] = useState<string | null>(null);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const validacion = validarRepresentante(r);
    if (validacion) {
      setErr(validacion);
      return;
    }
    setErr(null);
    onSave(normalizarRepresentante(r));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-lg bg-surface shadow-overlay">
        <div className="sticky top-0 flex items-center justify-between border-b bg-surface px-4 py-3">
          <h3 className="text-sm font-semibold text-fg">
            {initial ? "Editar representante" : "Agregar representante"}
          </h3>
          <button type="button" onClick={onCancel} className="rounded p-1 hover:bg-surface-2" aria-label="Cerrar">
            <X className="h-4 w-4" />
          </button>
        </div>
        <form onSubmit={submit} className="space-y-5 p-4">
          <RepresentanteFormFields r={r} setR={setR} />

          {err && (
            <div className="flex items-start gap-2 rounded-lg border bg-estado-warn-bg p-3 text-sm text-estado-warn">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{err}</span>
            </div>
          )}

          <div className="flex justify-end gap-2 border-t pt-3">
            <button
              type="button"
              onClick={onCancel}
              className="rounded border px-3 py-1.5 text-sm text-fg-muted hover:bg-surface-2"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="rounded bg-primary px-4 py-1.5 text-sm font-medium text-white hover:bg-primary-hover"
            >
              Guardar representante
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
